import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, Send, MapPin, Phone, Mail, Clock } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { submitQuote } from "@/lib/api";

const SERVICES = [
  "Full Color-Change Wrap",
  "Partial Wrap / Accents",
  "Paint Protection Film",
  "Ceramic Coating",
  "Window Tint",
  "Custom Decals / Graphics",
  "Commercial / Fleet",
];

const TIMELINES = ["ASAP", "Within 2 weeks", "Within a month", "Just researching"];
const BUDGETS = ["Under $1,000", "$1,000 – $3,000", "$3,000 – $6,000", "$6,000+"];

const Field = ({ label, children }) => (
  <label className="block">
    <span className="block font-mono text-[10px] tracking-[0.25em] text-steel uppercase mb-2">{label}</span>
    {children}
  </label>
);

const Contact = () => {
  const [form, setForm] = useState({
    client_name: "",
    email: "",
    phone: "",
    vehicle: "",
    service: "",
    timeline: "",
    budget: "",
    notes: "",
  });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setVal = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await submitQuote({
        client_name: form.client_name,
        email: form.email,
        phone: form.phone,
        source: "contact",
        service: form.service,
        timeline: form.timeline,
        budget: form.budget,
        vehicle_info: { vehicle_type: "", year_make_model: form.vehicle },
        notes: form.notes,
      });
      toast.success("Quote request sent", { description: "Expect a written quote within 48 hours." });
      setForm({ client_name: "", email: "", phone: "", vehicle: "", service: "", timeline: "", budget: "", notes: "" });
    } catch (err) {
      toast.error("Could not send your request", { description: "Please try again in a moment." });
    } finally {
      setSending(false);
    }
  };

  return (
    <main data-testid="contact-view" className="pt-24 pb-20 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-8">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
          <p className="font-mono text-xs tracking-[0.25em] text-velocity uppercase font-bold mb-3">Intake Terminal</p>
          <h1 className="font-display font-black uppercase tracking-tight text-3xl sm:text-6xl leading-none">
            Get A <span className="text-velocity">Written Quote.</span>
          </h1>
          <p className="mt-4 max-w-xl text-slate-400 leading-relaxed">
            Tell us about the vehicle and the vision. The studio replies within 48 hours with film options, pricing
            and the next available install window.
          </p>
        </motion.div>

        <div className="mt-10 grid grid-cols-1 lg:grid-cols-5 gap-6">
          <motion.form
            data-testid="contact-quote-form"
            onSubmit={handleSubmit}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="lg:col-span-3 rounded-2xl border border-edge bg-panel p-5 sm:p-8 space-y-4 sm:space-y-5"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field label="Full Name *">
                <Input data-testid="contact-name-input" required value={form.client_name} onChange={set("client_name")} placeholder="Alex Carter" className="bg-gunmetal border-edge" />
              </Field>
              <Field label="Phone">
                <Input data-testid="contact-phone-input" value={form.phone} onChange={set("phone")} placeholder="(555) 000-0000" className="bg-gunmetal border-edge" />
              </Field>
            </div>
            <Field label="Email *">
              <Input data-testid="contact-email-input" required type="email" value={form.email} onChange={set("email")} placeholder="you@email.com" className="bg-gunmetal border-edge" />
            </Field>
            <Field label="Vehicle Year, Make & Model">
              <Input data-testid="contact-vehicle-input" value={form.vehicle} onChange={set("vehicle")} placeholder="2023 Porsche 911 Carrera" className="bg-gunmetal border-edge" />
            </Field>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              <Field label="Service">
                <Select value={form.service} onValueChange={setVal("service")}>
                  <SelectTrigger data-testid="contact-service-select" className="bg-gunmetal border-edge">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent className="bg-gunmetal border-edge">
                    {SERVICES.map((s) => (
                      <SelectItem key={s} value={s} data-testid={`contact-service-option-${s.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Timeline">
                <Select value={form.timeline} onValueChange={setVal("timeline")}>
                  <SelectTrigger data-testid="contact-timeline-select" className="bg-gunmetal border-edge">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent className="bg-gunmetal border-edge">
                    {TIMELINES.map((t) => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Budget">
                <Select value={form.budget} onValueChange={setVal("budget")}>
                  <SelectTrigger data-testid="contact-budget-select" className="bg-gunmetal border-edge">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent className="bg-gunmetal border-edge">
                    {BUDGETS.map((b) => (
                      <SelectItem key={b} value={b}>{b}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            </div>
            <Field label="Notes / Special Requests">
              <Textarea
                data-testid="contact-notes-input"
                value={form.notes}
                onChange={set("notes")}
                placeholder="Colors you're considering, inspiration links, deadlines — anything that helps."
                className="bg-gunmetal border-edge min-h-[120px]"
              />
            </Field>
            <Button
              data-testid="contact-submit-btn"
              type="submit"
              disabled={sending}
              className="w-full bg-velocity hover:bg-velocitydark text-white font-semibold py-6 text-base"
            >
              {sending ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
              {sending ? "Sending..." : "Send Quote Request"}
            </Button>
          </motion.form>

          <motion.aside
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="lg:col-span-2 space-y-4"
          >
            <div className="rounded-2xl border border-edge bg-panel p-5 sm:p-7 carbon-bg">
              <p className="font-mono text-xs tracking-[0.25em] text-velocity uppercase font-bold mb-5">The Studio</p>
              <ul className="space-y-4 text-sm text-slate-300">
                <li className="flex items-start gap-3"><MapPin className="h-4 w-4 mt-0.5 text-velocity shrink-0" />20211 E US-412, STE 206<br />Springdale, AR 72764<br /><span className="text-slate-500">Climate-controlled install bay</span></li>
                <li className="flex items-start gap-3"><Phone className="h-4 w-4 mt-0.5 text-velocity shrink-0" /><a data-testid="contact-phone-link" href="tel:+14793100682" className="hover:text-white transition-colors">(479) 310-0682</a></li>
                <li className="flex items-start gap-3"><Mail className="h-4 w-4 mt-0.5 text-velocity shrink-0" /><a data-testid="contact-email-link" href="mailto:g5autorestyling@gmail.com" className="hover:text-white transition-colors">g5autorestyling@gmail.com</a></li>
                <li className="flex items-start gap-3"><Clock className="h-4 w-4 mt-0.5 text-velocity shrink-0" />Mon–Sat · 9:00 AM – 6:00 PM<br /><span className="text-slate-500">Sundays by appointment</span></li>
              </ul>
            </div>
            <div className="rounded-2xl border border-edge bg-panel p-5 sm:p-7">
              <p className="font-mono text-xs tracking-[0.25em] text-velocity uppercase font-bold mb-4">What Happens Next</p>
              <ol className="space-y-3 text-sm text-slate-400">
                {[
                  "We review your request and film availability.",
                  "You get a written quote with options within 48 hours.",
                  "Approve, drop a deposit, and we book your install window.",
                ].map((s, i) => (
                  <li key={i} className="flex gap-3">
                    <span className="font-display font-black text-velocity">{i + 1}.</span>
                    {s}
                  </li>
                ))}
              </ol>
            </div>
          </motion.aside>
        </div>
      </div>
      <section data-testid="quote-info" className="max-w-3xl mx-auto px-4 sm:px-8 mt-12 sm:mt-16 border-t border-edge pt-8 sm:pt-10">
        <h2 className="font-display font-bold text-xl sm:text-2xl tracking-tight mb-4">Get a Free Wrap, Tint or PPF Quote in Springdale, AR</h2>
        <div className="space-y-4 text-sm text-slate-400 leading-relaxed">
          <p>Tell us about your vehicle and what you're picturing, and the G5 crew will come back with a written quote — usually within 48 hours. The more detail you share (year, make, model, colors you're considering, links to inspiration), the more accurate your estimate will be. Quotes cover material, labor and any accent work, so the number you approve is the number you pay. We're located on US-412 in Springdale, a short drive from Rogers, Bentonville, Lowell and Fayetteville, with a climate-controlled install bay and secure overnight storage for multi-day projects.</p>
          <p>Not sure what your car needs yet? That's normal. Many customers start with a free consultation — we'll walk around the vehicle together, show you film samples in person, and map options to your budget, whether that's a full color-change wrap, stealth paint protection film, ceramic coating, window tint or a simple chrome delete. You can also book a time slot directly through our online calendar. Every job leaves with a workmanship warranty and aftercare instructions, and we're always a phone call away if you have questions down the road.</p>
        </div>
      </section>

    </main>
  );
};

export default Contact;
